import React from 'react';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';
import { BP_CARD_CLASSES } from './brandPresenceClasses';

/** Rows drawn while the job list loads — about one screen of it. */
const JOB_ROW_COUNT = 6;

function JobRowSkeleton({ index }: { index: number }) {
  return (
    <div className={`flex items-center gap-5 px-5 py-4 ${BP_CARD_CLASSES}`}>
      <SkeletonBlock className="h-9 w-9 flex-shrink-0 rounded-full" />
      <div className="grid flex-grow grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-4">
        <div className="space-y-2">
          <SkeletonBlock className={`h-4 ${index % 2 ? 'w-40' : 'w-56'}`} />
          <SkeletonBlock className="h-3 w-24" />
        </div>
        <div className="space-y-2">
          <SkeletonBlock className="h-2.5 w-14" />
          <SkeletonBlock className="h-3.5 w-28" />
        </div>
        <div className="space-y-2">
          <SkeletonBlock className="h-2.5 w-14" />
          <SkeletonBlock className="h-3.5 w-16" />
        </div>
        <SkeletonBlock className="h-4 w-4 rounded" />
      </div>
    </div>
  );
}

/** The job list while it loads: rows shaped like the job rows that replace them. */
export function JobListSkeleton() {
  return (
    <SkeletonRegion label="Loading jobs" className="space-y-3">
      {Array.from({ length: JOB_ROW_COUNT }, (_unused, index) => (
        <JobRowSkeleton key={index} index={index} />
      ))}
    </SkeletonRegion>
  );
}

/** One job while it loads: its summary card, then the list below it. */
export function JobDetailSkeleton() {
  return (
    <SkeletonRegion label="Loading this job" className="space-y-6">
      <div className={`${BP_CARD_CLASSES} p-6`}>
        <SkeletonBlock className="h-3 w-32" />
        <SkeletonBlock className="mt-3 h-7 w-64 rounded-lg" />
        <div className="mt-6 grid grid-cols-1 gap-4 rounded-xl bg-slate-50/80 p-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((cell) => (
            <div key={cell} className="space-y-2">
              <SkeletonBlock className="h-2.5 w-14" />
              <SkeletonBlock className="h-3.5 w-28" />
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {[0, 1, 2].map((tile) => (
            <div key={tile} className="space-y-3 rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
              <SkeletonBlock className="h-2.5 w-16" />
              <SkeletonBlock className="h-7 w-12 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {[0, 1, 2].map((row) => (
          <JobRowSkeleton key={row} index={row} />
        ))}
      </div>
    </SkeletonRegion>
  );
}
